import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import { usePermissions } from '@/hooks/usePermissions';
import {
  fetchSearchStats,
  listRecentSearches,
  logSearch,
  reindexOrganization,
  searchEntityMeta,
  searchGlobal,
  type CoreSearchHistory,
  type SearchGroup,
} from '@/lib/coreSearch';
import { useAuth } from '@/hooks/useAuth';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

export default function SearchTab({ commerceId, org }: Props) {
  const { user } = useAuth();
  const { has } = usePermissions();
  const [stats, setStats] = useState<{ total: number; byType: Record<string, number> }>({ total: 0, byType: {} });
  const [recent, setRecent] = useState<CoreSearchHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [reindexing, setReindexing] = useState(false);
  const [reindexMsg, setReindexMsg] = useState('');
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [groups, setGroups] = useState<SearchGroup[]>([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [s, r] = await Promise.all([fetchSearchStats(org.id), listRecentSearches(org.id, 12)]);
      setStats(s);
      setRecent(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const runReindex = async () => {
    setReindexing(true);
    setReindexMsg('');
    try {
      const res = await reindexOrganization({ tenantId: org.tenant_id, organizationId: org.id, commerceId });
      setReindexMsg(`${res.indexed} élément(s) indexé(s) : ${Object.entries(res.byType).map(([k, v]) => `${searchEntityMeta(k).label} ${v}`).join(' · ') || 'aucune source disponible'}`);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Réindexation impossible.');
    } finally {
      setReindexing(false);
    }
  };

  const runSearch = async () => {
    const q = query.trim();
    if (q.length < 2) return;
    setSearching(true);
    setSearched(true);
    try {
      const res = await searchGlobal({ organizationId: org.id, query: q, canAccess: (p) => has(p), limit: 60, userId: user?.id ?? null });
      setGroups(res.groups);
      await logSearch({ tenantId: org.tenant_id, organizationId: org.id, userId: user?.id ?? null, query: q, resultCount: res.total });
      listRecentSearches(org.id, 12).then(setRecent).catch(() => {});
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Recherche impossible.');
    } finally {
      setSearching(false);
    }
  };

  const typeRows = useMemo(
    () => Object.entries(stats.byType).sort((a, b) => b[1] - a[1]),
    [stats.byType],
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-xs text-foreground-500 max-w-3xl">
          <span className="font-semibold text-foreground-700">Search Engine interne</span> : une couche de recherche centralisée sur clients, entreprises, produits, services, documents, commandes, factures, rendez-vous et réservations.
          La recherche <span className="font-semibold text-foreground-700">respecte les permissions</span> — un utilisateur ne découvre jamais une ressource simplement parce qu’elle est indexée.
        </p>
        <button
          onClick={runReindex}
          disabled={reindexing}
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50"
        >
          <i className={reindexing ? 'ri-loader-4-line animate-spin' : 'ri-refresh-line'}></i>
          Réindexer
        </button>
      </div>

      {reindexMsg && (
        <div className="p-3 bg-primary-50 border border-primary-200 rounded-md text-xs text-primary-800 flex items-start gap-2">
          <i className="ri-checkbox-circle-line mt-0.5"></i><span>{reindexMsg}</span>
        </div>
      )}
      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-3">
          <i className="ri-database-2-line text-lg text-primary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.total}</p>
          <p className="text-xs text-foreground-500">Éléments indexés</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-3">
          <i className="ri-stack-line text-lg text-accent-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{typeRows.length}</p>
          <p className="text-xs text-foreground-500">Types couverts</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-3">
          <i className="ri-history-line text-lg text-secondary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{recent.length}</p>
          <p className="text-xs text-foreground-500">Recherches récentes</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-3">
          <i className="ri-shield-check-line text-lg text-primary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{has('*') ? 'Toutes' : 'Filtrées'}</p>
          <p className="text-xs text-foreground-500">Permissions appliquées</p>
        </div>
      </div>

      {/* Testeur de recherche */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center"><i className="ri-search-line"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Tester la recherche</h4>
        </div>
        <div className="p-4 space-y-3">
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm pointer-events-none"></i>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
                placeholder="Ex : Ahmed, FAC-2026, réservation…"
                className="w-full pl-9 pr-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors"
              />
            </div>
            <button onClick={runSearch} disabled={searching || query.trim().length < 2} className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2 justify-center">
              {searching ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-search-line"></i>} Rechercher
            </button>
          </div>

          {searched && groups.length === 0 && !searching && (
            <p className="text-xs text-foreground-400 text-center py-4">Aucun résultat. Vérifiez l’orthographe ou lancez une réindexation.</p>
          )}

          {groups.length > 0 && (
            <div className="space-y-3">
              {groups.map((g) => (
                <div key={g.key}>
                  <p className="text-[11px] font-semibold text-foreground-600 mb-1.5 flex items-center gap-1.5">
                    <span className={`w-5 h-5 rounded flex items-center justify-center ${g.className}`}><i className={`${g.icon} text-[11px]`}></i></span>
                    {g.label} <span className="text-foreground-400">({g.results.length})</span>
                  </p>
                  <div className="space-y-1">
                    {g.results.slice(0, 8).map((r) => (
                      <div key={`${r.entity_type}-${r.entity_id}`} className="flex items-center gap-2.5 px-3 py-2 rounded-md bg-background-100/60">
                        <i className={`${r.typeIcon} text-foreground-400`}></i>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-foreground-800 truncate">{r.title}</p>
                          {r.subtitle && <p className="text-[11px] text-foreground-400 truncate">{r.subtitle}</p>}
                        </div>
                      </div>
                    ))}
                    {g.results.length > 8 && <p className="text-[11px] text-foreground-400 px-3">+ {g.results.length - 8} autre(s)…</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Répartition de l'index */}
      {loading ? (
        <div className="flex items-center justify-center py-16"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
      ) : typeRows.length > 0 && (
        <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center"><i className="ri-bar-chart-horizontal-line"></i></span>
            <h4 className="text-sm font-semibold text-foreground-900">Contenu de l’index</h4>
          </div>
          <div className="p-4 flex flex-wrap gap-2">
            {typeRows.map(([type, count]) => {
              const meta = searchEntityMeta(type);
              return (
                <span key={type} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${meta.className}`}>
                  <i className={meta.icon}></i>{meta.label}<span className="font-bold">{count}</span>
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Recherches récentes */}
      {recent.length > 0 && (
        <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center"><i className="ri-history-line"></i></span>
            <h4 className="text-sm font-semibold text-foreground-900">Recherches récentes</h4>
          </div>
          <div className="divide-y divide-background-200/60">
            {recent.map((r) => (
              <div key={r.id} className="flex items-center gap-3 px-4 py-2.5">
                <i className="ri-search-line text-foreground-400"></i>
                <p className="flex-1 text-sm text-foreground-700 truncate">{r.query}</p>
                <span className="text-[11px] text-foreground-400 whitespace-nowrap">{r.result_count ?? 0} résultat(s)</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}